import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEPARTMENTS, DEPARTMENT_LABEL, primaryDepartment } from '@hub/shared';
import type { DepartmentOverview, OverviewKpi } from '@hub/shared';
import { api } from '../lib/api.js';
import type { Me } from '../lib/session.js';
import {
  EXCEPTION_LABEL,
  QUEUE_KIND_LABEL,
  REASON_LABEL,
  SEND_MODE_LABEL,
  label,
} from '../lib/labels.js';
import { num } from '../lib/format.js';
import { hrefFor } from '../lib/router.js';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  Pill,
  Tile,
} from '../components/ui.js';
import { Overview } from './Overview.js';

const formatKpi = (k: OverviewKpi): string => {
  if (k.value === null) return '—';
  if (typeof k.value === 'string') {
    return k.key === 'send_mode' ? label(SEND_MODE_LABEL, k.value) : k.value;
  }
  if (k.unit === 'pct') return `${num(k.value)} %`;
  if (k.unit === 'seconds') {
    return k.value < 120 ? `${num(k.value)} s` : `${num(Math.round(k.value / 60))} min`;
  }
  return num(k.value);
};

const queueText = (item: DepartmentOverview['queue'][number]): string => {
  if (item.kind === 'exception') return label(EXCEPTION_LABEL, item.label);
  if (item.kind === 'reason') return label(REASON_LABEL, item.label);
  if (item.kind === 'review_batch') return `${QUEUE_KIND_LABEL.review_batch}: ${item.label}`;
  return item.label;
};

function DepartmentView({ department }: { department: string }): JSX.Element {
  const q = useQuery<DepartmentOverview>({
    queryKey: ['overview-dept', department],
    queryFn: () => api.get(`/api/overview/department/${department}`),
    refetchInterval: 60_000,
  });
  if (q.isLoading) return <LoadingState what="přehled" />;
  if (q.isError || !q.data)
    return (
      <ErrorState
        error={q.error}
        title="Přehled se nepodařilo načíst"
        onRetry={() => void q.refetch()}
      />
    );
  const data = q.data;
  return (
    <>
      <div className="tiles" data-testid={`kpis-${department}`}>
        {data.kpis.map((k) => {
          const tile = (
            <Tile label={k.label} value={formatKpi(k)} note={k.hint} bad={k.tone === 'bad'} />
          );
          return k.link ? (
            <a key={k.key} href={hrefFor(k.link.screen, k.link.param)} data-testid={`kpi-${k.key}`}>
              {tile}
            </a>
          ) : (
            <div key={k.key} data-testid={`kpi-${k.key}`}>
              {tile}
            </div>
          );
        })}
      </div>
      <Panel title="Co čeká na vás" subtitle="Pracovní fronta oddělení">
        {data.queue.length === 0 ? (
          <EmptyState title="Nic nečeká" icon="check">
            Fronta oddělení je prázdná, není co řešit.
          </EmptyState>
        ) : (
          <ul className="plain">
            {data.queue.map((item, i) => (
              <li key={`${item.kind}-${item.label}-${i}`} data-testid="queue-item">
                {item.link ? (
                  <a href={hrefFor(item.link.screen, item.link.param)}>{queueText(item)}</a>
                ) : (
                  queueText(item)
                )}{' '}
                <Pill tone={item.kind === 'exception' ? 'warn' : 'neu'}>{num(item.count)}</Pill>
                {item.detail ? <small> {item.detail}</small> : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

export function MyOverview({ me }: { me: Me }): JSX.Element {
  const deps = me.user.departments;
  const isAdmin = deps.includes('admin');
  const own = DEPARTMENTS.filter((d) => isAdmin || deps.includes(d));
  const showOverall = isAdmin || deps.includes('marketing');
  const [tab, setTab] = useState<string>(primaryDepartment(deps));

  return (
    <>
      <PageHeader
        title="Můj přehled"
        description="Čísla a fronta práce pro vaše oddělení. Úplný přehled za celý hub najdete v záložce Celkově."
      />
      <div className="tabbar" role="tablist">
        {own.map((d) => (
          <button
            key={d}
            role="tab"
            aria-selected={tab === d}
            className={`tabbtn${tab === d ? ' on' : ''}`}
            onClick={() => setTab(d)}
            data-testid={`tab-overview-${d}`}
          >
            <span>
              <b>{DEPARTMENT_LABEL[d]}</b>
            </span>
          </button>
        ))}
        {showOverall ? (
          <button
            role="tab"
            aria-selected={tab === 'overall'}
            className={`tabbtn${tab === 'overall' ? ' on' : ''}`}
            onClick={() => setTab('overall')}
            data-testid="tab-overview-overall"
          >
            <span>
              <b>Celkově</b>
            </span>
          </button>
        ) : null}
      </div>
      {tab === 'overall' ? <Overview me={me} /> : <DepartmentView department={tab} />}
    </>
  );
}
